/*!
 * Copyright © 2022. Verizon Connect Ireland Limited. All rights reserved.
 */

import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { switchMap } from 'rxjs/operators';
import { EGeofenceDrawingControlShape } from '../../../geofence/models';
import {
  EGeofenceActions,
  GetPlaceDropdownCategories,
  SetGeofenceDrawingControlShape,
  SetGeofenceLatLng,
  SetSelectedGeofenceShape,
  ShowExistingGeofenceForEdit
} from '../../../geofence/store';
import { GoogleMapsShape } from '../../../maps/models';
import { ESidePanelContent, LatLng, MarkerShape } from '../../models';
import { ELayoutActions, OpenGeofencePanel, OpenSidePanel, OpenTroubleshootingPanel } from '../actions';
import { CloseInsightsPanel } from '../actions/insights-panel.actions';

@Injectable()
export class SidePanelEffects {
  public openTroubleshootingPanel$ = createEffect(() =>
    this._actions$.pipe(
      ofType<OpenTroubleshootingPanel>(ELayoutActions.OpenTroubleshootingPanel),
      switchMap(() => [new CloseInsightsPanel(), new OpenSidePanel(ESidePanelContent.DeviceHealthCheck)])
    )
  );

  public openGeofencePanel$ = createEffect(() =>
    this._actions$.pipe(
      ofType<OpenGeofencePanel>(ELayoutActions.OpenGeofencePanel),
      switchMap(action => [
        new CloseInsightsPanel(),
        new OpenSidePanel(ESidePanelContent.Geofence),
        new SetGeofenceLatLng(action.payload),
        new GetPlaceDropdownCategories()
      ])
    )
  );

  public showExistingGeofenceForEdit$ = createEffect(() =>
    this._actions$.pipe(
      ofType<ShowExistingGeofenceForEdit>(EGeofenceActions.ShowExistingGeofenceForEdit),
      switchMap(action => {
        const markerShape = {
          center: new LatLng(action.payload.center.Latitude, action.payload.center.Longitude),
          radius: action.payload.radius,
          shapes: action.payload.shapecoords[0]?.Points?.map(coord => new LatLng(coord.Latitude, coord.Longitude)),
          shapeColor: '#0077B4',
          shapeType: action.payload.shapetype
        };
        const googleMarkerShape: GoogleMapsShape = new MarkerShape(markerShape).toGoogleMapShape();
        const options = {
          strokeColor: '#000000',
          editable: true,
          draggable: true
        };
        googleMarkerShape.setOptions(options);
        const selectedShape: EGeofenceDrawingControlShape =
          googleMarkerShape instanceof google.maps.Polygon
            ? EGeofenceDrawingControlShape.Polygon
            : googleMarkerShape instanceof google.maps.Rectangle
            ? EGeofenceDrawingControlShape.Rectangle
            : EGeofenceDrawingControlShape.Circle;
        const actions: Action[] = [new SetSelectedGeofenceShape(googleMarkerShape), new SetGeofenceDrawingControlShape(selectedShape)];
        return actions;
      })
    )
  );

  constructor(private readonly _actions$: Actions) {}
}
